import { SidebarInset, SidebarProvider } from "@bowfast/ui/components/sidebar";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet, useLocation } from "@tanstack/react-router";
import { AnimatePresence } from "framer-motion";
import { AnchorIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "react-oidc-context";
import type { AppHeaderCrumb } from "~/components/app-header";
import { AppHeader } from "~/components/app-header";
import { AppLoader } from "~/components/app-loader";
import { AppSidebar } from "~/components/app-sidebar";
import { createSdk } from "~/lib/gql";
import { NotificationsProvider } from "~/lib/notifications-context";
import { OrgContext } from "~/lib/org-context";
import { ROLE_LABELS } from "~/lib/roles";

const SEGMENT_LABELS: Record<string, string> = {
  dashboard: "Dashboard",
  expeditions: "Expeditions",
  new: "New Expedition",
  vessels: "Vessels",
  crew: "Crew",
  routes: "Routes",
  certificates: "Certificates",
  skills: "Skills",
  notifications: "Notifications",
  reports: "Reports",
  settings: "Settings",
};

function deriveBreadcrumbs(pathname: string): AppHeaderCrumb[] {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return [{ label: "Dashboard" }];
  return segments.map((seg, i) => ({
    label: SEGMENT_LABELS[seg] ?? seg,
    ...(i < segments.length - 1 ? { href: `/${segments.slice(0, i + 1).join("/")}` } : {}),
  }));
}

export const Route = createFileRoute("/_authenticated")({
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const auth = useAuth();
  const { pathname } = useLocation();

  // ── Minimum loader duration — hides dark→light theme flicker ────────────
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setMinTimeElapsed(true), 2800);
    return () => clearTimeout(id);
  }, []);

  // Me query — must be called unconditionally (before any early returns)
  const { data: meData, isPending: mePending } = useQuery({
    queryKey: ["me"],
    queryFn: () => createSdk(auth.user?.access_token ?? "").Me(),
    enabled: !!auth.user?.access_token,
    staleTime: 5 * 60 * 1000,
  });

  // ── Derive identity (all hooks must be called unconditionally) ─────────────
  const gqlMe = meData?.me ?? null;
  const activeMembership = gqlMe?.memberships?.[0] ?? null;
  const activeOrg = activeMembership?.organisation ?? null;

  const oidcName =
    (auth.user?.profile?.name as string | undefined) ??
    (auth.user?.profile?.given_name as string | undefined) ??
    "Sailor";
  const oidcEmail = (auth.user?.profile?.email as string | undefined) ?? "";
  const oidcAvatar = (auth.user?.profile?.picture as string | undefined) ?? "";

  const roles: string[] = activeMembership?.roles?.length
    ? activeMembership.roles.map((r) => ROLE_LABELS[r] ?? r)
    : ["Skipper"];

  const user = {
    name: gqlMe ? `${gqlMe.firstName} ${gqlMe.lastName}`.trim() || oidcName : oidcName,
    roles,
    email: gqlMe?.email ?? oidcEmail,
    avatar: oidcAvatar,
  };

  // Teams — memoised so reference is stable; must be called before any early return
  const teams = useMemo(
    () =>
      gqlMe?.memberships?.length
        ? gqlMe.memberships.map((m) => ({
            name: m.organisation.name,
            logo: <AnchorIcon />,
            plan: m.roles.map((r) => ROLE_LABELS[r] ?? r).join(" · "),
          }))
        : [{ name: activeOrg?.name ?? "My Organisation", logo: <AnchorIcon />, plan: roles[0] ?? "Skipper" }],
    [gqlMe, activeOrg?.name, roles],
  );

  const orgContextValue = {
    orgId: activeOrg?.id ?? "",
    membership: activeMembership,
    me: gqlMe,
  };

  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated && !auth.error) {
      void auth.signinRedirect();
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.error, auth.signinRedirect]);

  const showLoader = auth.isLoading || !minTimeElapsed || (auth.isAuthenticated && mePending);

  // Signal to useTheme that it's safe to apply the resolved theme to the DOM
  useEffect(() => {
    if (!showLoader) {
      window.__BOWFAST_READY = true;
    }
  }, [showLoader]);

  if (!showLoader && !auth.isAuthenticated) {
    return null;
  }

  return (
    <>
      <AnimatePresence>{showLoader && <AppLoader key="app-loader" />}</AnimatePresence>

      {!showLoader && auth.isAuthenticated && (
        <OrgContext.Provider value={orgContextValue}>
          <NotificationsProvider>
            <SidebarProvider>
              <AppSidebar user={user} teams={teams} loading={mePending && !meData} />
              <SidebarInset>
                <AppHeader breadcrumbs={deriveBreadcrumbs(pathname)} user={user} />
                <Outlet />
              </SidebarInset>
            </SidebarProvider>
          </NotificationsProvider>
        </OrgContext.Provider>
      )}
    </>
  );
}
